// Une el formulario validado con el backend. F-01 completo, HU-01 a HU-08 y HU-36.
//
// `RegistroCaso` es la pantalla del prototipo, reutilizada tal cual (ADR-011). No
// sabe que existe una API: recibe `onGuardar` y `onEnviar` y ya. Toda la
// conversacion con el servidor —crear el caso, autoguardar con bloqueo optimista,
// traducir formas, manejar duplicados y faltantes— vive aqui.
//
// Esa separacion es deliberada: si el formulario llamara al API directamente,
// cualquier cambio del contrato obligaria a tocar la pantalla que el cliente
// aprobo, y ahi es donde se cuelan las divergencias con lo validado.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import RegistroCaso, { ENCABEZADO_DEL_REGISTRO } from './RegistroCaso';
import { Alert, Card, Modal, Button, Mono, PageHeader } from '../components/ui';
import { FormatoVerde } from '../components/Iconos';
import { useBorrador, ESTADO_DE_GUARDADO } from '../lib/useBorrador';
import { aCuerpoDeActualizacion, criterioPreliminarDeFormulario, desdeCaso, fechaAIso } from '../lib/mapeo';
import type { ValorRetirado } from '../lib/valoresRetirados';
import { useCatalogosOpcional, useZonaDelPais } from '../lib/CatalogosContexto';
import { proximoNumeroDeNotificacion, verCaso } from '../api/casos';
import { CasoIncompleto, PosibleDuplicado } from '../api/cliente';
import {
  conPersonasDelEspejo, guardarEspejo, leerEspejo, limpiarEnCurso, olvidarBorradorDeLaPestana, olvidarEspejo,
} from '../lib/borradorLocal';
import { olvidarRutaDelBorrador } from '../lib/rutaProfunda';
import { comoFechaDeInstante } from '../lib/fechas';
import { useAdjuntos } from '../lib/useAdjuntos';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import type { PosibleParecido } from '../lib/useBorrador';
import type { CasoDelFormulario, Faltante, RespuestaDelApi } from '../tipos';
import type { UsuarioSimulado } from '../lib/sesion';

/**
 * BUG-370 · QUE SE PINTA MIENTRAS EL BORRADOR RETOMADO LLEGA DEL SERVIDOR.
 *
 * Desde la bandeja el contenido llega hecho (`retomar.guardado.caso`) y el formulario se
 * pinta al instante. Tras RECARGAR la pestana solo llega el id —la ruta profunda o el
 * apuntador—, y el formulario montaba en blanco adoptando el id y su version: el siguiente
 * autoguardado pisaba el borrador del servidor con un formulario vacio. Ahora el contenido
 * sale del mismo `GET /casos/{id}` que da la version, y hasta tenerlo NO hay formulario
 * donde escribir (spec MG-ILE-370, Vacios 3).
 *
 *   · `lista`: hay con que pintar (contenido del servidor, del espejo o, si el caso ya no
 *     existe, `null` para abrir limpio).
 *   · `cargando`: el `GET` esta en vuelo.
 *   · `sin_permiso`: 403, el borrador no es suyo. No se adopta ni se pinta nada.
 *   · `sin_respaldo`: fallo de red o del servidor y sin espejo en esta pestana. Se ofrece
 *     reintentar; pintar vacio seria volver al defecto.
 */
type CargaDelBorrador =
  | { fase: 'lista'; contenido: unknown }
  | { fase: 'cargando' }
  | { fase: 'sin_permiso'; fallo: Fallo }
  | { fase: 'sin_respaldo'; fallo: Fallo };

/** El contenido que trae `retomar.guardado` (lo que dejo la bandeja). */
const contenidoDe = (guardado: unknown): unknown => (guardado as { caso?: unknown } | null)?.caso ?? null;

/** BUG-370 · el registro sin formulario: cargando el borrador, sin permiso o sin respaldo. */
function RegistroSinFormulario({ carga, onReintentar, onSalir }: {
  carga: Exclude<CargaDelBorrador, { fase: 'lista' }>;
  onReintentar: () => void;
  onSalir?: () => void;
}) {
  return (
    <>
      {/* El encabezado no desaparece mientras carga (lineamiento, Estados). */}
      <PageHeader {...ENCABEZADO_DEL_REGISTRO} icon={FormatoVerde} />
      {carga.fase === 'cargando' && (
        <Card>
          <p role="status" data-testid="registro-cargando" className="text-body text-ink-soft">
            Cargando el borrador…
          </p>
        </Card>
      )}
      {carga.fase === 'sin_permiso' && (
        // El `Alert` del catalogo aun no reenvia `data-testid` (lineamiento, Pendientes 1).
        <div data-testid="registro-sin-permiso" className="space-y-3">
          <Alert tone="warning" title="No tienes acceso a este caso">{carga.fallo.message}</Alert>
          {/* La misma salida que el pie del formulario: sin ella la pantalla no tiene nada que hacer. */}
          {onSalir && <Button variant="secondary" onClick={onSalir}>Volver a la bandeja</Button>}
        </div>
      )}
      {carga.fase === 'sin_respaldo' && (
        <div data-testid="registro-error" className="space-y-3">
          <Alert tone="danger" title="No se pudo traer el borrador" detalleTecnico={carga.fallo.detalleTecnico}>
            {carga.fallo.message}
            {carga.fallo.estado === 0 && ' Revisa tu conexión e intenta de nuevo.'}
            {' '}El borrador sigue guardado: no se abrirá otro.
          </Alert>
          <Button variant="secondary" icon={RefreshCw} onClick={onReintentar}>Reintentar</Button>
        </div>
      )}
    </>
  );
}

export default function RegistroConectado({
  usuario,
  onSalir,
  alQuedarRegistrado,
  retomar,
  onAbrirCaso,
  registrarGuardaDeSalida,
}: {
  usuario: UsuarioSimulado;
  /** Sale del registro. Recibe a donde ir cuando el destino no es la bandeja. */
  onSalir?: (destino?: string) => void;
  alQuedarRegistrado?: (respuesta: { caso_id: string; numero?: string }) => void;
  /** El borrador que se retoma: su id y lo que la bandeja dejo en el espejo. */
  retomar?: { casoId: string; guardado: unknown } | null;
  onAbrirCaso?: (casoId: string) => void;
  /**
   * BUG-376 (spec MG-ILE-376): el menu lateral vive FUERA de esta pantalla, en el esqueleto,
   * y navegaba sin preguntar. Por aqui la pantalla le presta su guarda de salida mientras
   * esta montada (y la retira al desmontarse): el esqueleto envuelve cada entrada del menu
   * con ella y el aviso que sale es este mismo, con sus mismos textos.
   */
  registrarGuardaDeSalida?: (guarda: ((irse: () => void) => void) | null) => void;
}) {
  const [duplicados, setDuplicados] = useState<PosibleParecido[] | null>(null);
  // Pagina visible del aviso de duplicados. Se reinicia cada vez que llega una lista
  // nueva: quedarse en la pagina 3 de una lista que ahora tiene 2 filas ensena un
  // cuadro vacio y parece que no hay parecidos.
  const [paginaDeDuplicados, setPaginaDeDuplicados] = useState(1);
  // Si el envio "y enviar igual" esta en vuelo. Ver el boton del pie.
  const [enviandoDuplicado, setEnviandoDuplicado] = useState(false);
  // Que parecidos ya ha visto y descartado el registrador, por caso_id. Sin esto el
  // aviso reaparece con cada autoguardado —cada pocos segundos mientras escribe— y se
  // vuelve ruido que se aprende a ignorar, que es la peor forma de perder una alerta.
  const [parecidosVistos, setParecidosVistos] = useState<string[]>([]);
  const [faltantesDelServidor, setFaltantesDelServidor] = useState<Faltante[] | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [registrado, setRegistrado] = useState<RespuestaDelApi | null>(null);
  /**
   * HU-36 · AVISAR ANTES DE SALIR CON CAMBIOS SIN GUARDAR (CP-36-04, 4-sep-2026).
   *
   * La historia lo pide en su escenario "Aviso de cambios sin guardar" y lo repite en los
   * criterios: "salir con cambios pendientes avisa antes de descartar". No existia ninguna
   * guarda: el boton de salir llamaba a `onSalir` directo y cerrar la pestana tampoco
   * avisaba. Bloqueaba CP-36-04, 05, 06 y el ultimo paso del 09.
   *
   * `salidaPedida` guarda LA SALIDA que se intento, no un booleano: por esta pantalla se
   * sale por dos puertas distintas —el boton de volver y el "abrir el caso existente" del
   * aviso de duplicado— y el dialogo tiene que saber a donde iba el usuario para llevarlo
   * si confirma.
   */
  const [salidaPedida, setSalidaPedida] = useState<(() => void) | null>(null);
  /**
   * Tras enviar, el borrador dejo de serlo: lo escrito se guardo y el caso paso a manos
   * del proceso (HU-08). Cualquier salida a partir de aqui es limpia, y avisar de
   * "cambios sin guardar" seria mentirle al registrador justo cuando acaba de terminar.
   *
   * Es estado propio y no `Boolean(registrado)` porque aquella se limpia al cerrar la
   * ventana de confirmacion, y el hecho de haber enviado no se deshace.
   */
  const [envioHecho, setEnvioHecho] = useState(false);
  /**
   * Lo que el FORMULARIO dice que aun no ha entregado. Es la mitad que no se ve desde
   * aqui: el estado del guardado va de `limpio` a `guardando` sin pisar `pendiente`, asi
   * que sin esta senal la ventana del retardo quedaria sin cubrir.
   */
  const [edicionPendiente, setEdicionPendiente] = useState(false);

  // AJ-EP (918-4): el intervalo de autoguardado es el del parametro, si el cliente lo recibe.
  const catalogos = useCatalogosOpcional();
  const borrador = useBorrador({ retardoMs: catalogos?.AUTOGUARDADO_MS });
  const { casoId, idActual, estado, abrir, guardarAhora, programarAutoguardado, enviar, adoptar, enSerie } =
    borrador;
  // HU-07 · lo que el servidor senala en CADA guardado. Ver el aviso mas abajo.
  const { posiblesDuplicados } = borrador;

  // HU-07 · la cadena REAL de subida (ADR-015): huella -> URL firmada -> PUT al
  // almacenamiento -> registrar la referencia.
  //
  // Vive aqui y no dentro de `RegistroCaso` porque necesita el `casoId` del servidor, y
  // esa pantalla no sabe que existe una API (ADR-011): recibe funciones ya cableadas.
  // Hasta el 26-ago-2026 el hook estaba escrito, probado a mano y SIN USAR POR NADIE —
  // la pantalla adjuntaba con un `prompt()` simulado, asi que ningun archivo llegaba
  // jamas al almacenamiento y el control C-01 daba por sustentado un caso sin evidencia.
  const adjuntos = useAdjuntos(casoId);
  // Se saca de su objeto para poder ponerlo en las dependencias del efecto de
  // rehidratacion: `useAdjuntos` devuelve un objeto nuevo en cada render, y depender de
  // el volveria a lanzar el `GET` sin parar. `sembrar` si es estable.
  const { sembrar: sembrarAdjuntos } = adjuntos;

  // BUG-370 · ver `CargaDelBorrador`. `guardado: null` es la RECARGA (`App` solo tiene el
  // id): se espera al servidor. Con lo que dejo la bandeja se pinta ya, como siempre.
  // HU-30-C4 (30-4): lo que `GET /casos/{id}` dice retirado; el registro lo pinta «(retirado)».
  const [valoresRetirados, setValoresRetirados] = useState<ValorRetirado[]>([]);
  const [carga, setCarga] = useState<CargaDelBorrador>(() => (
    retomar && !retomar.guardado
      ? { fase: 'cargando' }
      : { fase: 'lista', contenido: contenidoDe(retomar?.guardado) }
  ));
  // El `GET` fallo y el formulario se relleno con el espejo de esta pestana: se dice.
  const [falloDeCarga, setFalloDeCarga] = useState<Fallo | null>(null);
  // «Reintentar» tras un fallo sin respaldo vuelve a lanzar el efecto de adopcion.
  const [intentoDeCarga, setIntentoDeCarga] = useState(0);

  // El caso del formulario, para poder reintentar el envio tras aceptar el duplicado
  // sin depender de que el componente hijo vuelva a llamarnos.
  const ultimoCasoRef = useRef<CasoDelFormulario | null>(null);

  // T-47 · LA VENTANA DE ADOPCION. Retomar un borrador NUNCA puede crear un caso.
  //
  // Retomar adopta el id que YA existe, pero lo hace en asincrono: antes hay que
  // preguntarle al servidor la `version_bloqueo` (el `GET` del efecto de mas abajo).
  // Entre el montaje y esa respuesta `casoId` vale `null` AUNQUE EL CASO EXISTA, y esa
  // ventana era la que fabricaba duplicados: el autoguardado de la pantalla dispara a
  // los 1,2 s, `sincronizar` y `alAdjuntar` solo preguntaban `if (!casoId)`, y por ahi
  // se colaba un `POST /casos`. Con la red lenta, cada retomada dejaba en la base un
  // borrador nuevo, vacio y sin numero — n copias del mismo caso.
  //
  // La ventana se representa con la PROMESA de la adopcion en vuelo y no con un
  // booleano `retomar && !casoId`: ese booleano no sabria cerrarse cuando el caso ya no
  // existe (404), que es justo el unico escenario donde abrir uno nuevo SI es correcto,
  // y dejaria el borrador sin poder guardarse jamas. La promesa se resuelve en las TRES
  // ramas del efecto —id adoptado, 404 y respaldo del espejo—, asi que el ref vuelve a
  // `null` pase lo que pase.
  const adopcionRef = useRef<Promise<string | null> | null>(null);

  /**
   * Espera a que la adopcion en vuelo termine y devuelve el id adoptado; `null` si no
   * habia ninguna en vuelo o si no quedo nada que adoptar (el caso ya no existe).
   *
   * Es un `while` y no un solo `await` por el RELEVO: el efecto se rehace si cambia
   * `retomar`, y la adopcion que queda obsoleta resuelve `null`. Con un unico `await`
   * ese `null` se leeria como "aqui no hay caso" y abriria uno — el mismo duplicado
   * entrando por la otra puerta.
   */
  const esperarAdopcion = useCallback(async () => {
    let id = null;
    while (adopcionRef.current) id = await adopcionRef.current;
    return id;
  }, []);

  // El numero de notificacion que la pantalla muestra mientras el caso es borrador.
  // Sale de `GET /casos/series/{pais}` —es decir, de `serie_correlativo`— y no de una
  // cuenta del navegador: el formulario lo derivaba de la lista de casos que tuviera
  // cargada, y aqui esa lista esta vacia, asi que anunciaba `PE-001-2026` con la serie
  // de Peru en 73.
  //
  // Vive en este componente y no en el formulario porque es este el que sabe que hay
  // una API detras (ADR-011). `RegistroCaso` solo recibe el texto ya compuesto.
  const [pais, setPais] = useState(usuario?.pais || 'PE');
  // RN-82: las fechas de los posibles duplicados, en la zona del país del caso que se registra.
  // `zonaDe` es la misma fuente para lo que VIAJA (Bug #1086): la recepción y el inicio del
  // evento se mandan como la medianoche del país del caso, no la del navegador.
  const zonaDe = useZonaDelPais();
  const zona = zonaDe(pais);
  const [numeroPrevisto, setNumeroPrevisto] = useState<string | null>(null);

  useEffect(() => {
    // `vigente` cancela el efecto de un pais que ya no esta seleccionado. Sin esto, dos
    // cambios rapidos de pais pueden resolverse al reves y dejar en pantalla el numero
    // del pais anterior — el fallo clasico de la respuesta que llega tarde.
    let vigente = true;
    // Se limpia ANTES de pedir: mostrar el numero del pais anterior mientras carga el
    // del nuevo es peor que no mostrar ninguno, porque parece un dato ya confirmado.
    setNumeroPrevisto(null);
    // Se arranca en una promesa YA resuelta a proposito: desde T-45 la consulta valida
    // el codigo de pais antes de componer la URL y puede fallar SINCRONAMENTE. Llamarla
    // en seco dejaria ese fallo fuera del `.catch` de abajo —el que decide que no poder
    // PREVISUALIZAR el numero no es motivo para romper el registro— y reventaria el
    // efecto entero.
    Promise.resolve()
      .then(() => proximoNumeroDeNotificacion(pais))
      .then((serie) => {
        if (vigente) setNumeroPrevisto(serie.proximo_numero);
      })
      .catch(() => {
        // Se traga a proposito y NO se muestra error. Este numero es informativo: el
        // definitivo lo asigna el envio (RN-01). Bloquear o alarmar por no poder
        // PREVISUALIZARLO impediria registrar una sospecha, que es una obligacion legal.
        // El campo se queda con su rotulo "se asigna al enviar", que sigue siendo cierto.
        if (vigente) setNumeroPrevisto(null);
      });
    return () => {
      vigente = false;
    };
  }, [pais]);

  /**
   * Abre el borrador en el servidor y devuelve su id, o `null` si aun falta lo minimo.
   *
   * SE EXTRAJO de `sincronizar` el 26-ago-2026 porque ADJUNTAR tambien lo necesita.
   * Hasta entonces el caso solo nacia al guardar, y quien pulsaba "Adjuntar" antes del
   * primer guardado mandaba la peticion a `/casos/null/adjuntos/url-de-subida` — un 422
   * que en pantalla se leia como "Hay datos que el servidor no acepta" y no decia nada
   * del problema real.
   *
   * HU-01 fija el minimo para abrir: pais y fecha de recepcion. Los dos tienen valor por
   * defecto en el formulario, asi que en la practica siempre estan.
   */
  const abrirBorrador = useCallback(
    async (caso: CasoDelFormulario) => {
      if (!caso?.pais || !caso?.fechaRecepcion) return null;
      const abierto = await abrir({
        paisEvento: caso.pais,
        fechaRecepcion: fechaAIso(caso.fechaRecepcion, zonaDe(caso.pais)),
        criterioPreliminar: criterioPreliminarDeFormulario(caso.graveSugerido),
        declaracionResponsable: caso.declaracion?.trim() || undefined,
      });
      // `null` = el caso ya habia nacido mientras esta llamada iba en camino (T-49). No
      // es un error: se sigue con el id que ya existe y se mandan las secciones contra
      // EL MISMO caso, que es a lo que venia este ciclo de guardado.
      //
      // El `guardarAhora` va AQUI DENTRO y no basta con caer al de abajo: aquel cuelga
      // de `abierto`, asi que salir con un `return` pelado se lo saltaria y lo tecleado
      // no viajaria — en silencio, con el boton "Guardar" respondiendo como si hubiera
      // guardado. Es el fallo que HU-36 prohibe por escrito.
      //
      // Hoy esta rama NO se alcanza: entre `idActual()` y las guardas de `abrir` no hay
      // ningun punto de suspension, asi que `casoIdRef` no puede cambiar en ese hueco.
      // Se escribe correcta igualmente: el dia que alguien meta un `await` antes de
      // `abrir`, esto despierta como perdida de datos y no como un caso duplicado.
      if (!abierto) {
        const yaNacido = idActual();
        if (yaNacido) await guardarAhora(aCuerpoDeActualizacion(caso, zonaDe(caso.pais)), caso);
        return yaNacido;
      }
      guardarEspejo(abierto.caso_id, caso, abierto.version_bloqueo);
      // Y a continuacion el resto de secciones, que `POST /casos` no acepta.
      await guardarAhora(aCuerpoDeActualizacion(caso, zonaDe(caso.pais)), caso);
      return abierto.caso_id;
    },
    [abrir, guardarAhora, idActual, zonaDe],
  );

  /**
   * HU-07. Adjuntar ABRIENDO el borrador si hace falta.
   *
   * El caso tiene que existir antes de subir: la URL firmada es de un blob que cuelga
   * de el, y la referencia se registra contra el. Se abre aqui en vez de pedirle al
   * usuario que guarde primero, porque "guarda antes de adjuntar" es una regla del
   * sistema, no del negocio — y HU-01 no la pide.
   *
   * ⚠ ADJUNTAR SUBE LA VERSION DEL CASO, y hay que recogerla. `POST /casos/{id}/adjuntos`
   * responde el borrador entero, `version_bloqueo` incluida: el caso de uso termina en
   * `casos.guardar(caso)` y, como `caso.adjuntar()` no toca la columna, la incrementa el
   * trigger `tg_caso_version_bloqueo` de la base.
   *
   * Hasta el 26-ago-2026 esa respuesta se usaba solo para pintar la fila del adjunto y la
   * version se tiraba, porque `useAdjuntos` y `useBorrador` son hooks distintos y no se
   * hablan. Resultado: adjuntar un archivo y pulsar "Guardar borrador" daba SIEMPRE 409
   * —"El caso cambio mientras lo editabas"— sin que hubiera nadie mas editando. Con dos
   * adjuntos el desfase era de dos versiones, que es como se detecto.
   */
  const alAdjuntar = useCallback(
    async (archivo: File, { contieneDatoDePersonas = false, caso }: { contieneDatoDePersonas?: boolean; caso?: CasoDelFormulario } = {}) => {
      // BUG-380 · el archivo de 0 bytes se rechaza ANTES de todo lo que sigue: esperar la
      // adopcion, abrir el borrador y ponerse en serie con el autoguardado. La spec
      // (`ui/MG-ILE-380.md`) dice "ninguna peticion": sin esta puerta, un formulario nuevo
      // abria un caso en el servidor solo para rechazar un archivo vacio.
      if (adjuntos.rechazarSiEstaVacio(archivo)) return null;
      // T-47 · con una adopcion en vuelo el caso EXISTE, solo que su id todavia no ha
      // llegado: se espera y el adjunto cae donde tiene que caer. Abrir uno aqui dejaba
      // la evidencia colgando de un borrador paralelo que el registrador no volveria a
      // ver — y el caso que si mira seguiria sin sustento para el control C-01.
      // `idActual()` y no `casoId`: el estado llega tarde y este callback puede venir de
      // un render anterior (T-49). Con el estado, un caso que YA existe se lee como
      // inexistente y se abre otro.
      let id = idActual() ?? (await esperarAdopcion()) ?? idActual();
      if (!id) {
        // Sin caso no hay nada que abrir: adjuntar antes de escribir una sola casilla
        // no tiene a que colgarse.
        if (!caso) return null;
        try {
          id = await abrirBorrador(caso);
        } catch (error) {
          setFallo(aFallo(error));
          return null;
        }
      }
      // BUG 698 · EN SERIE CON EL AUTOGUARDADO (QA, CP-REG-11 paso 2). Adjuntar con un
      // `PUT` en vuelo terminaba en 409 `conflicto_de_concurrencia`: los dos escriben la
      // misma fila y el segundo encuentra la version movida. El que perdia era el
      // adjunto, con el binario YA subido al almacenamiento y la referencia sin
      // registrar — "se pierde el adjunto sin entender por que".
      //
      // `enSerie` espera al `PUT` en vuelo (y a la cola de uno) y, mientras esto corre,
      // encola el autoguardado que dispare. LA ADOPCION VA DENTRO: el guardado encolado
      // sale al soltar el turno, y si adoptaramos fuera saldria con la version vieja.
      return enSerie(async () => {
        const resultado = await adjuntos.agregar(archivo, { contieneDatoDePersonas }, id);
        // Solo si el servidor confirmo: si `agregar` fallo devuelve `null`, y ahi la
        // version buena sigue siendo la que ya teniamos.
        if (id && typeof resultado?.version_bloqueo === 'number') {
          adoptar(id, resultado.version_bloqueo);
        }
        return resultado;
      });
    },
    [idActual, esperarAdopcion, abrirBorrador, adjuntos, adoptar, enSerie],
  );

  /**
   * #377 · MG-ILE-377 · VARIOS ARCHIVOS: el mismo camino que `alAdjuntar` —esperar la
   * adopcion, abrir el borrador si no existe y ponerse en serie con el autoguardado—, con UNA
   * peticion de lote. Si toda la seleccion son archivos vacios no se abre nada (BUG-380).
   */
  const alAdjuntarVarios = useCallback(
    async (archivos: File[], { contieneDatoDePersonas = false, caso }: { contieneDatoDePersonas?: boolean; caso?: CasoDelFormulario } = {}) => {
      if (adjuntos.rechazarSiTodosEstanVacios(archivos)) return null;
      let id = idActual() ?? (await esperarAdopcion()) ?? idActual();
      if (!id) {
        if (!caso) return null;
        try {
          id = await abrirBorrador(caso);
        } catch (error) {
          setFallo(aFallo(error));
          return null;
        }
      }
      return enSerie(async () => {
        const resultado = await adjuntos.agregarVarios(archivos, { contieneDatoDePersonas }, id);
        if (id && typeof resultado?.version_bloqueo === 'number') adoptar(id, resultado.version_bloqueo);
        return resultado;
      });
    },
    [idActual, esperarAdopcion, abrirBorrador, adjuntos, adoptar, enSerie],
  );

  /**
   * HU-07 · QUITAR UN ADJUNTO TAMBIEN SUBE LA VERSION, y hay que recogerla
   * (reportado por el operador, 9-sep-2026).
   *
   * Es el mismo defecto que `alAdjuntar` cerro el 26-ago y por el mismo motivo: el
   * `DELETE` responde el borrador entero —`version_bloqueo` incluida, porque el caso de
   * uso termina en `casos.guardar(caso)` y el trigger `tg_caso_version_bloqueo` la
   * incrementa— y `quitar` se pasaba EN CRUDO a la pantalla, tirando esa respuesta.
   *
   * Efecto: borrar una evidencia y esperar al autoguardado daba 409 "El caso cambio
   * mientras lo editabas" sin que hubiera nadie mas editando. Y como el boton admitia
   * varios clics, el desfase era de una version POR CLIC.
   */
  const alQuitarAdjunto = useCallback(
    // BUG 698 · en serie, por lo mismo que `alAdjuntar`: el `DELETE` sube la version
    // igual que el `POST` —el trigger no distingue—, asi que la carrera con el `PUT` en
    // vuelo es la misma y tiene que cerrarse en las dos puertas.
    async (huellaSha256: string) => enSerie(async () => {
      const borrador = await adjuntos.quitar(huellaSha256);
      // Solo si el servidor confirmo: si `quitar` fallo —o si el clic se ignoro por
      // haber otro borrado en vuelo— devuelve `null`, y ahi la version buena sigue
      // siendo la que ya teniamos.
      const id = idActual();
      if (id && typeof borrador?.version_bloqueo === 'number') {
        adoptar(id, borrador.version_bloqueo);
      }
      return borrador;
    }),
    [adjuntos, idActual, adoptar, enSerie],
  );

  /**
   * HU-01 + HU-36. Un solo camino para el guardado manual y el autoguardado: HU-36
   * insiste en que el autoguardado "guarda borrador, no envia", y si fueran dos
   * caminos uno acabaria saltandose una validacion.
   */
  const sincronizar = useCallback(
    async (caso: CasoDelFormulario, { inmediato = false }: { inmediato?: boolean } = {}) => {
      ultimoCasoRef.current = caso;
      setFallo(null);

      // T-47 · si hay una adopcion en vuelo, este guardado es del caso RETOMADO: se
      // espera a su id en vez de abrir uno nuevo. No se pierde nada de lo tecleado —
      // `ultimoCasoRef` ya lo retiene y este mismo ciclo guarda en cuanto el id llega—,
      // y vale igual para el guardado manual que para el automatico: los dos entran por
      // aqui, asi que el invariante "con adopcion pendiente nadie abre" se cumple en un
      // solo sitio.
      // Ver la nota de `alAdjuntar`: se decide con el ref. El segundo `idActual()` cubre
      // la apertura que termino MIENTRAS se esperaba la adopcion.
      const id = idActual() ?? (await esperarAdopcion()) ?? idActual();

      // Primer guardado: el caso todavia no existe en el servidor. `abrirBorrador`
      // devuelve el id, o `null` si aun falta lo minimo de HU-01 (pais y fecha).
      if (!id) {
        await abrirBorrador(caso);
        return;
      }

      const cuerpo = aCuerpoDeActualizacion(caso, zonaDe(caso.pais));
      if (inmediato) await guardarAhora(cuerpo, caso);
      else programarAutoguardado(cuerpo, caso);
    },
    [idActual, esperarAdopcion, abrirBorrador, guardarAhora, programarAutoguardado, zonaDe],
  );

  const alGuardar = useCallback((caso: CasoDelFormulario) => sincronizar(caso, { inmediato: true }).catch((e) => setFallo(aFallo(e))), [sincronizar]);

  /** HU-08. El envio real, con los dos desvios de HU-07 y HU-01. */
  const intentarEnvio = useCallback(
    async (caso: CasoDelFormulario | null, { aceptandoDuplicado = false }: { aceptandoDuplicado?: boolean } = {}) => {
      setFallo(null);
      setFaltantesDelServidor(null);
      try {
        // Se fuerza un guardado antes: el usuario pudo teclear en los 2 s que el
        // autoguardado espera, y enviar sin eso perderia lo ultimo escrito.
        if (caso) await sincronizar(caso, { inmediato: true });
        const respuesta = await enviar({ aceptandoDuplicado });
        setDuplicados(null);
        setEnvioHecho(true);
        setRegistrado(respuesta as RespuestaDelApi);
        alQuedarRegistrado?.(respuesta as { caso_id: string; numero?: string });
      } catch (error) {
        if (error instanceof PosibleDuplicado) {
          setPaginaDeDuplicados(1);
          // HU-07: no se bloquea, se ADVIERTE. La decision es del usuario.
          setDuplicados(error.candidatos as PosibleParecido[]);
        } else if (error instanceof CasoIncompleto) {
          // El servidor es la autoridad sobre que falta. El formulario valida en
          // paralelo, pero si los dos discrepan manda este.
          setFaltantesDelServidor(error.faltantes);
        } else {
          setFallo(aFallo(error));
        }
      }
    },
    [sincronizar, enviar, alQuedarRegistrado],
  );

  const alEnviar = useCallback((caso: CasoDelFormulario) => intentarEnvio(caso), [intentarEnvio]);

  // Rehidratacion tras recargar. El CONTENIDO sale del espejo local (`GET /casos/{id}`
  // no devuelve la seccion 7, que es dato L2); la VERSION se le pregunta al servidor.
  //
  // ⚠ Antes la version tambien salia del espejo, y eso hacia la desincronizacion
  // IRRECUPERABLE: el espejo solo se refresca al guardar, asi que si el servidor habia
  // movido `version_bloqueo` por otro camino —adjuntar una evidencia—, recargar volvia a
  // adoptar la version vieja y el primer guardado daba 409 otra vez. Justo lo que el
  // mensaje de conflicto recomienda hacer ("recarga y vuelve a aplicarlo") no funcionaba,
  // y el borrador quedaba muerto sin salida desde la interfaz.
  //
  // 404 = el caso ya no existe (base recreada, borrador purgado). Se olvida el espejo en
  // vez de arrastrar un id fantasma: la pantalla arranca limpia, que es lo que el usuario
  // espera al recargar sobre un caso que ya no esta.
  //
  // ⚠ ESTE `GET` TRAE TAMBIEN LOS ADJUNTOS, y hasta el 28-ago-2026 se tiraban (T-44
  // §3): la seccion de evidencias de un borrador retomado salia vacia aunque el caso
  // tuviera archivos, y el aviso de duplicado por huella no los conocia, asi que
  // re-adjuntar el mismo archivo lo subia dos veces. Se siembran EN LA MISMA respuesta
  // que da la version: hacerlo en otro sitio significaria un segundo `GET` del mismo
  // caso en el mismo instante, y adelantarse a adoptar el id dejaria la lista colgada
  // de un caso que la pantalla todavia no reconoce como suyo.
  //
  // T-47 · ADEMAS, este efecto PUBLICA la adopcion en `adopcionRef` mientras esta en
  // vuelo. Cada rama devuelve el id que quedo adoptado —o `null` si no quedo ninguno—
  // para que quien la espere sepa contra que caso guardar; y `finally` cierra la
  // ventana siempre, tambien cuando el `GET` falla, porque una ventana que no se cierra
  // deja el borrador sin poder guardarse nunca.
  //
  // BUG-370 · TRAS RECARGAR, ESTE MISMO `GET` TRAE TAMBIEN EL CONTENIDO. `retomar` llega
  // sin `guardado` (solo el id de la ruta profunda o del apuntador), y cada rama deja
  // dicho que pintar en `carga`: el contenido del servidor, el del espejo, limpio (404) o
  // nada (403, o sin red y sin espejo).
  //
  // RN-82 (Bug #1086) · EL CONTENIDO DEL SERVIDOR SE TRADUCE CON LA ZONA DE SU PAIS. La
  // recepcion y el inicio del evento son la medianoche del pais del caso, y solo leidas en
  // esa zona vuelven con su dia (la de Chile es todavia el dia anterior en Lima). La zona
  // llega con los catalogos: si el `GET` vuelve antes, el caso espera en `porTraducir`.
  const [porTraducir, setPorTraducir] = useState<{ id: string; caso: RespuestaDelApi } | null>(null);
  const zonasListas = !catalogos?.cargando;
  // No se limpia al relanzar el efecto de abajo: adoptar el id lo relanza, y eso pasa justo
  // mientras el caso espera. Lo que vale es que siga siendo el borrador que se retoma.
  const casoQueSeRetoma = retomar?.casoId;
  useEffect(() => {
    if (!porTraducir || !zonasListas) return;
    const { id, caso } = porTraducir;
    setPorTraducir(null);
    if (id !== casoQueSeRetoma) return;
    setCarga({ fase: 'lista', contenido: conPersonasDelEspejo(id, desdeCaso(caso, zonaDe(caso.pais_evento))) });
  }, [porTraducir, zonasListas, zonaDe, casoQueSeRetoma]);

  useEffect(() => {
    if (casoId || !retomar?.casoId) return;
    let vigente = true;
    const id = retomar.casoId;
    // La recarga llega con `guardado: null`; la bandeja, con su version y su contenido.
    const sinContenido = !retomar.guardado;

    /** Sin contenido en la mano y el servidor no lo dio: que se puede pintar, si algo. */
    const sinElServidor = (error: unknown): string | null => {
      const falloDelGet = aFallo(error);
      if (falloDelGet.estado === 403) {
        setCarga({ fase: 'sin_permiso', fallo: falloDelGet });
        return null;
      }
      const espejo = leerEspejo(id);
      if (!espejo?.caso) {
        setCarga({ fase: 'sin_respaldo', fallo: falloDelGet });
        return null;
      }
      adoptar(id, espejo.versionBloqueo ?? 1);
      setFalloDeCarga(falloDelGet);
      setCarga({ fase: 'lista', contenido: espejo.caso });
      return id;
    };

    const adopcion = verCaso(id)
      .then((caso) => {
        if (!vigente) return null;
        adoptar(id, caso.version_bloqueo);
        sembrarAdjuntos(caso.adjuntos);
        // HU-30-C4 (30-4): los valores de catálogo retirados que el borrador conserva.
        setValoresRetirados(Array.isArray(caso.valores_retirados) ? caso.valores_retirados : []);
        // RN-82: se traduce cuando se sepa la zona de su país (ver `porTraducir`).
        if (sinContenido) setPorTraducir({ id, caso });
        return id;
      })
      .catch((error) => {
        if (!vigente) return null;
        if (error?.estado === 404) {
          olvidarEspejo(id);
          limpiarEnCurso();
          // Y el apuntador y la ruta (BUG-370): si no, cada recarga volveria a buscarlo.
          olvidarBorradorDeLaPestana();
          olvidarRutaDelBorrador();
          if (sinContenido) setCarga({ fase: 'lista', contenido: null });
          // Sin id que adoptar: quien esperaba recibe `null` y queda libre para abrir un
          // caso nuevo, que es lo correcto cuando el que se retomaba ya no existe.
          return null;
        }
        if (sinContenido) return sinElServidor(error);
        // Cualquier otro fallo (sin red, servidor caido) NO debe impedir seguir
        // editando: se cae al espejo, que es peor pero no bloquea. Y adoptar aqui cierra
        // la ventana igual que la rama buena: el id existe, solo que la version es la
        // del espejo.
        adoptar(id, (retomar.guardado as { versionBloqueo?: number } | null)?.versionBloqueo ?? 1);
        return id;
      })
      .finally(() => {
        // La comparacion de identidad es por el relevo: si `retomar` cambio mientras
        // esta adopcion viajaba, el ref ya guarda la NUEVA y borrarla dejaria a los que
        // esperan creyendo que no hay ninguna en vuelo.
        if (adopcionRef.current === adopcion) adopcionRef.current = null;
      });
    adopcionRef.current = adopcion;
    return () => {
      vigente = false;
      if (adopcionRef.current === adopcion) adopcionRef.current = null;
    };
  }, [casoId, retomar, adoptar, sembrarAdjuntos, intentoDeCarga]);

  /**
   * HU-36 · que cuenta como "cambios sin guardar", y por que son DOS listas.
   *
   * Navegar DENTRO de la aplicacion no aborta una peticion en vuelo: si el autoguardado
   * ya sali&oacute; hacia el servidor (`GUARDANDO`), terminara igual y el trabajo esta a
   * salvo. Avisar ahi seria un falso positivo, y ademas rompe CP-36-06 —"cambiar de
   * seccion para forzar el autoguardado y volver a salir: tampoco se muestra
   * advertencia"— si el usuario sale rapido.
   *
   * Cerrar la PESTANA si la aborta. Por eso el aviso del navegador incluye tambien
   * `GUARDANDO`: ahi el trabajo se pierde de verdad.
   *
   * `ERROR` y `CONFLICTO` entran en las dos: en ambos el servidor NO tiene lo ultimo
   * escrito, y HU-36 es explicita en que el peor fallo es el silencioso —"no me deja
   * creer que mi trabajo esta a salvo cuando no lo esta"—.
   */
  const sinGuardar = !envioHecho && (
    edicionPendiente
    || estado === ESTADO_DE_GUARDADO.PENDIENTE
    || estado === ESTADO_DE_GUARDADO.ERROR
    || estado === ESTADO_DE_GUARDADO.CONFLICTO
  );
  const sinGuardarAlCerrar = sinGuardar || (!envioHecho && estado === ESTADO_DE_GUARDADO.GUARDANDO);

  // Cerrar o recargar la pestana. El navegador NO deja poner texto propio —lo ignora y
  // muestra el suyo—: lo unico que se controla es si pregunta o no.
  useEffect(() => {
    if (!sinGuardarAlCerrar) return undefined;
    const alCerrar = (evento: BeforeUnloadEvent) => {
      evento.preventDefault();
      evento.returnValue = '';
    };
    window.addEventListener('beforeunload', alCerrar);
    return () => window.removeEventListener('beforeunload', alCerrar);
  }, [sinGuardarAlCerrar]);

  /**
   * Envuelve una salida. Sin nada pendiente se sale directo —CP-36-06 lo comprueba— y
   * con algo pendiente se pregunta primero.
   *
   * `setSalidaPedida(() => irse)` y no `setSalidaPedida(irse)`: React trata una funcion
   * pasada a un `set` como ACTUALIZADOR y la llamaria, con lo que se saldria de la
   * pantalla en el acto en vez de guardarla para despues.
   */
  const salirConGuarda = useCallback(
    (irse: () => void) => {
      if (typeof irse !== 'function') return;
      if (!sinGuardar) { irse(); return; }
      // `useState` con una funcion la INTERPRETA como actualizador: se envuelve para
      // guardarla como valor. Sin esto React la llamaria en vez de almacenarla.
      setSalidaPedida(() => irse);
    },
    [sinGuardar],
  );

  // BUG-376: el menu lateral pasa por la MISMA guarda que el boton de salir. Se presta en
  // cada cambio de `salirConGuarda` (cambia con `sinGuardar`) y se retira al desmontar.
  useEffect(() => {
    if (!registrarGuardaDeSalida) return undefined;
    registrarGuardaDeSalida(salirConGuarda);
    return () => registrarGuardaDeSalida(null);
  }, [registrarGuardaDeSalida, salirConGuarda]);

  const confirmarSalida = useCallback(() => {
    const irse = salidaPedida;
    setSalidaPedida(null);
    irse?.();
  }, [salidaPedida]);

  /**
   * "Continuar y enviar igual" BLOQUEA mientras la peticion viaja (operador, 9-sep-2026).
   *
   * Antes era `onClick={() => intentarEnvio(...)}`: se disparaba sin esperar, y el cuadro
   * seguia vivo y pulsable. Es la misma rendija que se cerro en la ventana de
   * confirmacion del registro — con un agravante: aqui el envio fuerza un guardado
   * inmediato ANTES de enviar, asi que son dos viajes y la ventana dura mas.
   *
   * El cuadro NO se cierra hasta que termina: la ventana ya cubre la pantalla, asi que
   * dejarla puesta bloquea el formulario sin tapar nada aparte. Si el envio va bien, la
   * pantalla de confirmacion la sustituye; si falla —faltantes del servidor, red— hay que
   * salir para que se vea el aviso, y de eso se encarga `intentarEnvio`.
   *
   * ⚠ SPINNER SIN PORCENTAJE, como se pidio, y ademas es lo honesto: el servidor no
   * informa del avance y cualquier barra seria inventada.
   */
  const continuarYEnviarIgual = useCallback(async () => {
    if (enviandoDuplicado) return;
    setEnviandoDuplicado(true);
    try {
      await intentarEnvio(ultimoCasoRef.current, { aceptandoDuplicado: true });
    } finally {
      setEnviandoDuplicado(false);
    }
  }, [enviandoDuplicado, intentarEnvio]);

  /** Paginado del aviso: 5 filas. Una lista larga deja de leerse y se acepta sin mirar. */
  const POR_PAGINA_DUPLICADOS = 5;
  const totalDeDuplicados = duplicados?.length ?? 0;
  const paginasDeDuplicados = Math.max(1, Math.ceil(totalDeDuplicados / POR_PAGINA_DUPLICADOS));
  const duplicadosVisibles = (duplicados ?? []).slice(
    (paginaDeDuplicados - 1) * POR_PAGINA_DUPLICADOS,
    paginaDeDuplicados * POR_PAGINA_DUPLICADOS,
  );

  /**
   * EL CUADRO DE ARRIBA NO REPITE LO QUE EL CAMPO YA DICE (operador, 9-sep-2026).
   *
   * Al teclear un correo sin arroba, el campo lo marcaba —"Al correo le falta la arroba"—
   * y ADEMAS salia arriba del todo un cuadro ROJO: "No se pudo guardar · 'enriquez34a' no
   * tiene formato de correo valido". Dos veces lo mismo, y la segunda en rojo y en la
   * cabecera, que se lee como si algo grave hubiera pasado con el caso entero.
   *
   * Pasa porque el autoguardado manda igual: el formulario avisa pero no bloquea, el
   * servidor rechaza el dato y el 422 sube hasta aqui.
   *
   * ⚠ SE CALLA SOLO `invariante_violada`, y esa precision es el punto. Ese codigo son las
   * reglas de negocio del dominio —formato del correo, coherencia de los conteos— y el
   * formulario las valida TODAS en local, asi que el registrador ya las esta viendo junto
   * a su campo.
   *
   * Todo lo demas SIGUE saliendo, y tiene que seguir: la red caida (`estado === 0`), un
   * 500, un codigo de catalogo que ya no existe. Nada de eso lo dice ningun campo, y
   * callarlo convertiria un guardado fallido en un fallo silencioso — que es justo lo que
   * HU-36 llama el peor de todos.
   *
   * El trabajo no queda desprotegido aunque el cuadro no salga: el estado pasa a `ERROR`,
   * y eso alimenta la guarda de salida de HU-36, que avisa antes de abandonar la pantalla
   * con cambios sin guardar.
   */
  const loDiceElCampo = fallo?.estado === 422 && fallo?.cuerpo?.codigo === 'invariante_violada';

  // Se descartan por caso_id, no en bloque: si mientras escribe aparece un parecido
  // NUEVO, ese si vuelve a avisar aunque ya hubiera descartado los anteriores.
  const parecidosSinDescartar = (posiblesDuplicados ?? []).filter(
    (d: PosibleParecido) => !parecidosVistos.includes(d.caso_id),
  );

  if (carga.fase !== 'lista') {
    return (
      <RegistroSinFormulario
        carga={carga}
        onSalir={onSalir ? () => onSalir() : undefined}
        onReintentar={() => {
          setCarga({ fase: 'cargando' });
          setIntentoDeCarga((n) => n + 1);
        }}
      />
    );
  }

  return (
    <>
      {/* BUG-370 · el `GET` del borrador fallo y se muestra la copia de esta pestana. */}
      {falloDeCarga && (
        <div data-testid="registro-error" className="mb-4">
          <Alert tone="warning" title="No se pudo traer el borrador guardado" detalleTecnico={falloDeCarga.detalleTecnico}>
            {falloDeCarga.message}
            {falloDeCarga.estado === 0 && ' Revisa tu conexión e intenta refrescar.'}
            {' '}Se muestra la última copia guardada en este equipo, sin las evidencias adjuntas; lo que
            guardes va al mismo borrador.
          </Alert>
        </div>
      )}

      {fallo && !loDiceElCampo && (
        <div data-testid="registro-error-envio" className="mb-4">
          <Alert tone="danger" title="No se pudo guardar">
            {fallo.message}
            {fallo.estado === 0 && ' Revisa tu conexión; lo escrito sigue en esta pantalla.'}
          </Alert>
        </div>
      )}

      {/*
        El texto decia "Alguien mas guardo este borrador" y remataba con "recarga y vuelve
        a aplicarlo". Las dos mitades eran malas: la error mas frecuente no era otra
        persona sino esta misma pestana —adjuntar movia la version sin que la pantalla se
        enterara—, y recargar no arreglaba nada porque la version se releia del espejo
        local. Se acusaba a un tercero inexistente y se mandaba a un remedio que no cura.
        Ahora la recarga SI recupera la version del servidor, asi que el consejo es
        honesto; y se nombra la otra pestana antes que a "alguien", que es lo probable.
      */}
      {estado === ESTADO_DE_GUARDADO.CONFLICTO && (
        <div data-testid="registro-error-envio" className="mb-4">
          <Alert tone="warning" title="Este borrador cambió mientras lo editabas">
            El caso se guardó desde otra pestaña u otra persona después de que lo abrieras. Tus
            últimos cambios no se guardaron: copia lo que escribiste, recarga la página y vuelve a aplicarlo.
          </Alert>
        </div>
      )}

      {/*
        HU-07 · ADVERTENCIA DE DUPLICADO MIENTRAS SE CAPTURA (CP-07-20, 4-sep-2026).

        La HU la pide asi: "Dado que registro producto, lote, notificante y fecha /
        Cuando coinciden exactamente con un caso ya existente / Entonces el sistema
        advierte". El aviso existia, pero solo salia al ENVIAR, con el 409. QA capturo
        los cuatro datos, no vio nada, y anoto "no advierte de duplicado"; con eso se
        bloquearon ademas CP-07-21, 22 y 23, que parten de haber visto la advertencia.

        POR QUE UN AVISO EN LA PAGINA Y NO LA VENTANA QUE YA HABIA. El ultimo paso de
        CP-07-20 comprueba que "el caso no se bloquea ni se cierra: la advertencia no
        impide seguir capturando". Una ventana modal atrapa el foco y hace justo lo
        contrario. La modal se queda para el ENVIO —ahi si hay que decidir antes de
        seguir— y aqui va un aviso que se lee sin soltar el teclado.

        Las salidas que la HU enumera: CONTINUAR (descartar el aviso y seguir
        capturando) y ABRIR EL EXISTENTE. La tercera —"crear un seguimiento"— pide
        enlazar un caso con otro, y hoy no existe ni el vinculo en la base ni el
        endpoint: queda planteado al operador, no inventado aqui.
      */}
      {parecidosSinDescartar.length > 0 && (
        <Alert tone="warning" title="Este caso se parece a otro ya registrado">
          <p>
            Coinciden producto, lote, notificante y fecha. No se bloquea nada: si de verdad son
            casos distintos, sigue capturando con normalidad.
          </p>
          <ul className="mt-2 space-y-1">
            {parecidosSinDescartar.map((d: PosibleParecido) => (
              <li key={d.caso_id} className="flex items-center gap-2">
                <span>
                  <Mono>{d.numero ?? 'sin número'}</Mono> · {d.producto} · {d.notificante} ·{' '}
                  {comoFechaDeInstante(d.fecha_recepcion, zona)}
                </span>
                {onAbrirCaso && (
                  <button
                    type="button"
                    // La otra puerta de salida de esta pantalla. Pasa por la misma guarda
                    // que el boton de volver: irse a mirar otro caso descarta lo ultimo
                    // escrito igual de rapido (HU-36).
                    onClick={() => salirConGuarda(() => onAbrirCaso(d.caso_id))}
                    className="underline text-marca-texto hover:no-underline"
                  >
                    abrir el caso existente
                  </button>
                )}
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() =>
              setParecidosVistos((previos) => [
                ...previos,
                ...parecidosSinDescartar.map((d) => d.caso_id),
              ])
            }
            className="mt-2 underline hover:no-underline"
          >
            Entendido, continuar con este caso
          </button>
        </Alert>
      )}

      {faltantesDelServidor && (
        <Alert tone="warning" title={`Faltan ${faltantesDelServidor.length} datos para enviar`}>
          <ul className="mt-1 space-y-1">
            {faltantesDelServidor.map((f) => (
              <li key={f.campo}>
                <strong>{f.seccion}</strong> · {f.detalle}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <RegistroCaso
        usuario={usuario}
        numeroPrevisto={numeroPrevisto}
        onPaisCambiado={setPais}
        // BUG-370: el de la bandeja o, tras recargar, el que devolvio el servidor.
        borrador={carga.contenido}
        onGuardar={alGuardar}
        onEnviar={alEnviar}
        onCambiosPendientes={setEdicionPendiente}
        onSalir={() => salirConGuarda(() => onSalir?.())}
        adjuntos={adjuntos.adjuntos}
        onAdjuntar={alAdjuntar}
        onAdjuntarVarios={alAdjuntarVarios}
        cupoExcedidoDeAdjunto={adjuntos.cupoExcedido}
        presupuestoDeAdjuntos={adjuntos.textoDelPresupuesto}
        subiendoAdjunto={adjuntos.subiendo}
        rechazosDeAdjunto={adjuntos.rechazos}
        falloDeAdjunto={adjuntos.fallo}
        onLimpiarRechazos={adjuntos.limpiarRechazos}
        onQuitarAdjunto={alQuitarAdjunto}
        quitandoAdjunto={adjuntos.quitando}
        limitesDeAdjuntos={adjuntos.configuracion}
        valoresRetirados={valoresRetirados}
      />

      {/* HU-07: "elijo continuar -> el caso sigue su curso normal". */}
      <Modal
        open={Boolean(duplicados?.length)}
        onClose={() => setDuplicados(null)}
        eyebrow="Registro del caso · Posible duplicado"
        tone="warning"
        title="Este caso se parece a otro ya registrado"
        footer={
          <>
            <Button variant="secondary" disabled={enviandoDuplicado}
              onClick={() => setDuplicados(null)}>
              Revisar antes de enviar
            </Button>
            {/* CP-07-40. La tercera salida faltaba, y es la que resuelve el caso de
                verdad: si el duplicado ES el mismo evento, el registrador necesita
                IR a verlo. Con solo dos botones se quedaba adivinando entre
                "continuar" y "revisar" sin poder comprobar nada. */}
            {duplicados?.length === 1 && onAbrirCaso && (
              <Button variant="secondary" disabled={enviandoDuplicado}
                onClick={() => salirConGuarda(() => onAbrirCaso(duplicados[0].caso_id))}>
                Abrir el caso existente
              </Button>
            )}
            <Button variant="primary" disabled={enviandoDuplicado} onClick={continuarYEnviarIgual}>
              {enviandoDuplicado ? (
                // `role="status"`: el lector de pantalla anuncia que sigue trabajando; el
                // icono es decorativo y se le esconde, o leeria su nombre.
                <span role="status" className="inline-flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                  Enviando…
                </span>
              ) : 'Continuar y enviar igual'}
            </Button>
          </>
        }
      >
        <p className="text-body text-ink-soft">
          Coinciden producto, lote, notificante y fecha. Si de verdad son casos distintos, puedes
          continuar.
        </p>
        {/* TABLA Y NO LISTA (operador, 9-sep-2026). Con una docena de parecidos, cuatro
            datos concatenados por fila obligan a leer cada linea entera para comparar. En
            columnas el ojo compara en vertical y se ve de un vistazo cual es cual.

            Y PAGINADA de 5 en 5: la lista larga desbordaba el cuadro y empujaba los
            botones fuera de la vista, asi que habia que desplazarse DENTRO de la ventana
            para poder decidir. Una advertencia que no se lee entera se acepta sin mirar,
            que es exactamente lo que RN-11 existe para evitar.

            `relative`: la zona recorta lo `absolute` de sus celdas (un `sr-only`), que si no
            agranda la pagina en horizontal (WCAG 1.4.10, BUG-1099). */}
        <div className="relative mt-3 overflow-x-auto border border-hairline rounded-xl">
          <table className="w-full text-meta">
            <caption className="sr-only">
              Casos que se parecen a éste: coinciden producto, lote, notificante y fecha
            </caption>
            <thead className="bg-canvas">
              <tr>
                {['N.º de caso', 'Producto', 'Notificante', 'Recepción', ''].map((titulo, i) => (
                  <th key={titulo || `acciones-${i}`} scope="col"
                    className="font-semibold text-meta text-ink-soft px-3 py-2 text-left whitespace-nowrap">
                    {titulo}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {duplicadosVisibles.map((d) => (
                <tr key={d.caso_id} className="border-t border-hairline">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <Mono>{d.numero ?? 'sin número'}</Mono>
                  </td>
                  <td className="px-3 py-2">{d.producto}</td>
                  <td className="px-3 py-2">{d.notificante}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {comoFechaDeInstante(d.fecha_recepcion, zona)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {/* Con varios candidatos el botón del pie no sabría a cuál ir, así que
                        cada fila lleva el suyo. */}
                    {onAbrirCaso && (
                      <button
                        type="button"
                        disabled={enviandoDuplicado}
                        onClick={() => onAbrirCaso(d.caso_id)}
                        className="underline text-marca-texto hover:no-underline disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        abrir
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <p className="text-meta text-ink-soft">
            Mostrando{' '}
            <Mono className="text-ink">
              {`${(paginaDeDuplicados - 1) * POR_PAGINA_DUPLICADOS + 1}–${(paginaDeDuplicados - 1) * POR_PAGINA_DUPLICADOS + duplicadosVisibles.length}`}
            </Mono>{' '}
            de <Mono className="text-ink">{totalDeDuplicados}</Mono>{' '}
            {totalDeDuplicados === 1 ? 'caso parecido' : 'casos parecidos'}.
          </p>
          {/* Los controles solo aparecen si hay mas de una pagina: con tres parecidos,
              unos botones siempre apagados son ruido. */}
          {paginasDeDuplicados > 1 && (
            <nav className="ml-auto flex items-center gap-2" aria-label="Paginación de los casos parecidos">
              <Button size="sm" variant="secondary" disabled={paginaDeDuplicados <= 1}
                onClick={() => setPaginaDeDuplicados((p) => p - 1)}>
                ← Anterior
              </Button>
              <span className="text-meta text-ink-soft whitespace-nowrap">
                Página <Mono className="text-ink">{paginaDeDuplicados}</Mono> de{' '}
                <Mono className="text-ink">{paginasDeDuplicados}</Mono>
              </span>
              <Button size="sm" variant="secondary" disabled={paginaDeDuplicados >= paginasDeDuplicados}
                onClick={() => setPaginaDeDuplicados((p) => p + 1)}>
                Siguiente →
              </Button>
            </nav>
          )}
        </div>
      </Modal>

      {/* HU-36 · "salir con cambios pendientes avisa antes de descartar" (CP-36-04). */}
      <Modal
        open={Boolean(salidaPedida)}
        onClose={() => setSalidaPedida(null)}
        data-testid="registro-aviso-sin-guardar"
        eyebrow="Registro del caso · Cambios sin guardar"
        tone="warning"
        title="Tienes cambios sin guardar"
        footer={
          <>
            {/* Permanecer es lo PRIMERO y lo secundario a la vez: es la salida segura,
                y CP-36-05 comprueba que al elegirla no se pierde ni un caracter. */}
            <Button variant="secondary" onClick={() => setSalidaPedida(null)} data-testid="registro-aviso-sin-guardar-permanecer">
              Permanecer en el caso
            </Button>
            <Button variant="primary" onClick={confirmarSalida} data-testid="registro-aviso-sin-guardar-salir">
              Salir de todos modos
            </Button>
          </>
        }
      >
        <p className="text-body text-ink-soft">
          Lo último que escribiste todavía no se guardó. Si sales ahora, el caso
          conserva lo que se guardó hasta el último autoguardado, pero <b>los cambios más
          recientes se pierden</b>.
        </p>
        {estado === ESTADO_DE_GUARDADO.ERROR && (
          <p className="mt-2 text-meta text-amber-700">
            Además, el último intento de guardado falló. Si es un corte de red, quedarte y
            esperar unos segundos suele bastar para que se guarde solo.
          </p>
        )}
      </Modal>

      {/* HU-08: "recibo confirmacion con el numero de caso asignado". */}
      <Modal
        open={Boolean(registrado)}
        onClose={() => setRegistrado(null)}
        eyebrow="Registro del caso · Envío"
        tone="success"
        title="Caso enviado"
        footer={
          <Button variant="primary" onClick={() => onSalir?.()}>
            Entendido
          </Button>
        }
      >
        <p className="text-body text-ink-soft">
          El caso quedó registrado con el número <Mono>{registrado?.numero}</Mono>. Se generó su
          expediente y se avisó al Comité.
        </p>
      </Modal>
    </>
  );
}
